import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import useSelfServiceFlags from "hooks/useSelfServiceFlags";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackAdminAccess from "./useHasStackAdminAccess";

const STACK_UPDATE_ACCESS_TYPES = [Action.StackManage];

const hasStackUpdateAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(STACK_UPDATE_ACCESS_TYPES, viewer, spaceId);

type StackForUpdate = Pick<Stack, "spaceDetails" | "space"> &
  Partial<Pick<Stack, "blueprintDeployment">>;

/**
 * Hook that returns a function to check if the current user can update a stack.
 * A user can update a stack if they have stack management permissions or
 * if they have specific stack update access through custom access roles.
 * Stacks created from a template deployment (blueprintDeployment) cannot be updated.
 * @returns {(stack?: StackForUpdate) => AccessResult} Function that takes a stack and returns AccessResult
 */
const useCanUpdateStack = (): ((stack?: StackForUpdate) => AccessResult) => {
  const hasAdminAccess = useHasStackAdminAccess();
  const { viewer } = useTypedContext(AccountContext);
  const { enableSelfServiceV2 } = useSelfServiceFlags();

  return useCallback(
    (stack) => {
      if (!stack) {
        return { result: false };
      }

      const blueprintCheck = checkTemplateDeploymentAccess(enableSelfServiceV2, stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      const hasAccess =
        hasAdminAccess(stack).result ||
        hasStackUpdateAccess(viewer, stack.spaceDetails?.id || stack.space);

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasAdminAccess, viewer, enableSelfServiceV2]
  );
};

export default useCanUpdateStack;
